// src/Pages/EventDetails.jsx
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Banner from "../Components/Banner";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useEvents } from "../hooks/useEvents";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const stripHtml = (html = "") =>
  html
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const EventDetails = () => {
  const { slug } = useParams();
  const { getEventBySlug } = useEvents();

  // Loading state
  const [loading, setLoading] = useState(true);

  // Simulate loading for better UX
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 800); // 0.8 second delay for skeleton visibility

    return () => clearTimeout(timer);
  }, []);

  // Get event using slug
  const event = getEventBySlug(slug);

  if (!event && !loading) {
    return (
      <div className="container py-5 text-center">
        {/* <h2>Event not found</h2>
        <p className="text-muted mb-4">
          The requested event could not be found.
        </p> */}
      </div>
    );
  }

  // Use short description as meta description
  const metaDescription = event
    ? stripHtml(event.description) ||
      `Join us for ${event.title} at Sydney Sri Ayyappa Swami Centre`
    : "Loading event details...";

  return (
    <>
      <Helmet>
        {/* Basic SEO */}
        <title> Event Details - Sydney Sri Ayyappa Swami Centre</title>
        <meta
          name="description"
          content="Sydney Sri Ayyappa Swami Centre is a spiritual hub for Lord Ayyappa devotees, offering poojas, bhajans, festivals, and community services in Sydney."
        />
        <meta
          name="keywords"
          content="Sydney Sri Ayyappa Swami Centre, Ayyappa Temple Sydney, Lord Ayyappa Temple Australia, Hindu Temple in Sydney, Ayyappa Pooja Sydney, Swami Ayyappa Devotees Sydney, Sabarimala Ayyappa Sydney, Hindu Religious Centre Sydney, Ayyappa Bhajans Sydney, Indian Hindu Temple Sydney"
        />

        {/* Open Graph */}
        <meta property="og:title" content="Sydney Sri Ayyappa Swami Centre" />
        <meta
          property="og:description"
          content="Sydney Sri Ayyappa Swami Centre is a spiritual hub for Lord Ayyappa devotees, offering poojas, bhajans, festivals, and community services in Sydney."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sydneysriayyappa.com/" />

        {/* Social Links */}
        <meta
          property="og:see_also"
          content="https://www.youtube.com/@SydneySriAyyappaSwamiCentre"
        />
        <meta
          property="og:see_also"
          content="https://www.instagram.com/sydneysriayyappa/"
        />
        <meta
          property="og:see_also"
          content="https://www.facebook.com/sydneyayyappa"
        />

        {/* Facebook  */}

        <meta
          property="fb:admins"
          content="https://www.facebook.com/sydneyayyappa"
        />

        {/* Instagram */}
        <meta
          name="instagram:title"
          content="Sydney Sri Ayyappa Swami Centre"
        />
        <meta
          name="instagram:description"
          content="Sydney Sri Ayyappa Swami Centre is a spiritual hub for Lord Ayyappa devotees, offering poojas, bhajans, festivals, and community services in Sydney."
        />

        <meta name="instagram:site" content="@sydneysriayyappa" />
      </Helmet>

      <div className="container-fluid p-0">
        {/* Banner Section - Show skeleton if loading */}
        {loading ? (
          <div className="position-relative">
            <Skeleton height={400} width="100%" />
            <div className="position-absolute top-50 start-50 translate-middle text-center w-100 px-3">
              <Skeleton
                height={60}
                width={300}
                style={{ marginBottom: "20px" }}
              />
              <Skeleton
                height={20}
                width={200}
                style={{ marginBottom: "10px" }}
              />
              <Skeleton height={20} width={150} />
            </div>
          </div>
        ) : (
          <Banner bannerData={event} />
        )}

        {/* Event Details Section with responsive layout */}
        <section>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="event-details-section bg-white py-4 py-md-5">
              <div className="container">
                {/* Event Info Cards */}
                <motion.div
                  className="row mb-4 mb-md-5"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  {/* Date Card */}
                  <div className="col-12 col-md-4 mb-3 mb-md-0">
                    <div className="card border-0 shadow-lg h-100">
                      <div className="card-body d-flex align-items-center p-4 ">
                        {loading ? (
                          <Skeleton
                            circle
                            width={40}
                            height={40}
                            style={{ marginRight: "15px" }}
                          />
                        ) : (
                          <i className="bi bi-calendar3 iconcustom fs-3 me-3"></i>
                        )}
                        <div>
                          {loading ? (
                            <>
                              <Skeleton
                                height={20}
                                width={100}
                                style={{ marginBottom: "5px" }}
                              />
                              <Skeleton height={25} width={120} />
                            </>
                          ) : (
                            <>
                              <h6 className="text-muted mb-1">Date</h6>
                              <h5 className="mb-0 fw-bold text-dark">
                                {event.date}
                              </h5>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Time Card */}
                  <div className="col-12 col-md-4 mb-3 mb-md-0">
                    <div className="card border-0 shadow-lg h-100">
                      <div className="card-body d-flex align-items-center p-4">
                        {loading ? (
                          <Skeleton
                            circle
                            width={40}
                            height={40}
                            style={{ marginRight: "15px" }}
                          />
                        ) : (
                          <i className="bi bi-clock iconcustom fs-3 me-3"></i>
                        )}
                        <div>
                          {loading ? (
                            <>
                              <Skeleton
                                height={20}
                                width={100}
                                style={{ marginBottom: "5px" }}
                              />
                              <Skeleton height={25} width={120} />
                            </>
                          ) : (
                            <>
                              <h6 className="text-muted mb-1">Time</h6>
                              <h5 className="mb-0 fw-bold text-dark">
                                {event.time}
                              </h5>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Venue Card */}
                  <div className="col-12 col-md-4">
                    <div className="card border-0 shadow-lg h-100">
                      <div className="card-body d-flex align-items-center p-4">
                        {loading ? (
                          <Skeleton
                            circle
                            width={40}
                            height={40}
                            style={{ marginRight: "15px" }}
                          />
                        ) : (
                          <i className="bi bi-geo-alt iconcustom fs-3 me-3"></i>
                        )}
                        <div>
                          {loading ? (
                            <>
                              <Skeleton
                                height={20}
                                width={100}
                                style={{ marginBottom: "5px" }}
                              />
                              <Skeleton height={25} width={150} />
                            </>
                          ) : event && event.venue ? (
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                event.venue
                              )}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-decoration-none"
                              style={{ color: "inherit" }}
                            >
                              <h6 className="text-muted mb-1">Venue</h6>
                              <h5 className="mb-0 fw-bold text-dark">
                                {event.venue}
                              </h5>
                            </a>
                          ) : (
                            <>
                              <h6 className="text-muted mb-1">Venue</h6>
                              <h5 className="mb-0 fw-bold text-dark">
                                {event?.venue || "Venue not specified"}
                              </h5>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Content Section */}
                <div className="d-block d-lg-none">
                  {/* Mobile Image - Skeleton while loading */}
                  <motion.div
                    className="mb-4"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                  >
                    {loading ? (
                      <Skeleton
                        height={250}
                        width="100%"
                        style={{ borderRadius: "10px" }}
                      />
                    ) : (
                      <div className="image-container rounded overflow-hidden shadow">
                        <img
                          src={event.image}
                          alt={event.title}
                          className="img-fluid w-100 object-fit-cover"
                          onError={(e) => {
                            e.target.src =
                              "https://via.placeholder.com/600x250/f8f9fa/6c757d?text=Event+Image";
                          }}
                        />
                      </div>
                    )}
                  </motion.div>

                  {/* Mobile Content */}
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
                  >
                    {/* Event Description - Skeleton while loading */}
                    {loading ? (
                      <div className="mb-3 px-3">
                        <Skeleton
                          count={3}
                          height={20}
                          width="100%"
                          style={{ marginBottom: "10px" }}
                        />
                      </div>
                    ) : (
                      <motion.p
                        className="mb-3 lh-base fs-6 px-3"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{
                          duration: 0.6,
                          delay: 0.4,
                          ease: "easeOut",
                        }}
                      >
                        {event.description}
                      </motion.p>
                    )}

                    {/* Event Details - Skeleton while loading */}
                    {loading ? (
                      <div className="px-3">
                        <Skeleton
                          height={20}
                          width="90%"
                          style={{ marginBottom: "10px" }}
                        />
                        <Skeleton
                          height={20}
                          width="95%"
                          style={{ marginBottom: "10px" }}
                        />
                        <Skeleton
                          height={20}
                          width="85%"
                          style={{ marginBottom: "10px" }}
                        />
                        <Skeleton
                          height={20}
                          width="92%"
                          style={{ marginBottom: "10px" }}
                        />
                        <Skeleton
                          height={20}
                          width="80%"
                          style={{ marginBottom: "10px" }}
                        />
                        <Skeleton
                          height={20}
                          width="88%"
                          style={{ marginBottom: "10px" }}
                        />
                      </div>
                    ) : (
                      <motion.div
                        className="lh-base fs-6 cms-content px-3 align-justify"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{
                          duration: 0.6,
                          delay: 0.5,
                          ease: "easeOut",
                        }}
                        dangerouslySetInnerHTML={{
                          __html: event.description1 || "",
                        }}
                      />
                    )}
                  </motion.div>
                </div>

                {/* Desktop View: Newspaper-style layout */}
                <div className="d-none d-lg-block position-relative">
                  {/* Floating image on left for desktop - Skeleton while loading */}
                  <motion.div
                    className="float-start me-4 mb-3"
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                    style={{
                      shapeOutside: "margin-box",
                      shapeMargin: "1rem",
                    }}
                  >
                    {loading ? (
                      <Skeleton
                        height={350}
                        width={450}
                        style={{ borderRadius: "10px" }}
                      />
                    ) : (
                      <div className="image-container rounded overflow-hidden shadow">
                        <img
                          src={event.image}
                          alt={event.title}
                          className="img-fluid w-100 object-fit-cover"
                          style={{
                            height: "350px",
                          }}
                          onError={(e) => {
                            e.target.src =
                              "https://via.placeholder.com/400x600/f8f9fa/6c757d?text=Event+Image";
                          }}
                        />
                      </div>
                    )}
                  </motion.div>

                  {/* Wrapped text content for desktop */}
                  <motion.div
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
                  >
                    {/* Event Description - Skeleton while loading */}
                    {loading ? (
                      <div className="mb-2">
                        <Skeleton
                          height={20}
                          width="100%"
                          style={{ marginBottom: "10px" }}
                        />
                        <Skeleton
                          height={20}
                          width="95%"
                          style={{ marginBottom: "10px" }}
                        />
                        <Skeleton
                          height={20}
                          width="90%"
                          style={{ marginBottom: "20px" }}
                        />
                      </div>
                    ) : (
                      <motion.p
                        className="mb-2 lh-lg fs-5 "
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{
                          duration: 0.6,
                          delay: 0.4,
                          ease: "easeOut",
                        }}
                      >
                        {event.description}
                      </motion.p>
                    )}

                    {/* Event Details - Skeleton while loading */}
                    {loading ? (
                      <div>
                        <Skeleton
                          height={20}
                          width="100%"
                          style={{ marginBottom: "15px" }}
                        />
                        <Skeleton
                          height={20}
                          width="95%"
                          style={{ marginBottom: "15px" }}
                        />
                        <Skeleton
                          height={20}
                          width="98%"
                          style={{ marginBottom: "15px" }}
                        />
                        <Skeleton
                          height={20}
                          width="92%"
                          style={{ marginBottom: "15px" }}
                        />
                        <Skeleton
                          height={20}
                          width="96%"
                          style={{ marginBottom: "15px" }}
                        />
                        <Skeleton
                          height={20}
                          width="90%"
                          style={{ marginBottom: "15px" }}
                        />
                        <Skeleton
                          height={20}
                          width="94%"
                          style={{ marginBottom: "15px" }}
                        />
                        <Skeleton
                          height={20}
                          width="85%"
                          style={{ marginBottom: "15px" }}
                        />
                      </div>
                    ) : (
                      <motion.div
                        className="lh-lg fs-5 cms-content align-justify text-break"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{
                          duration: 0.6,
                          delay: 0.5,
                          ease: "easeOut",
                        }}
                        dangerouslySetInnerHTML={{
                          __html: event.description1 || "",
                        }}
                      />
                    )}
                  </motion.div>

                  {/* Clear float for desktop design */}
                  <div className="clearfix"></div>
                </div>
              </div>
            </div>
          </motion.div>
        </section>
      </div>
    </>
  );
};

export default EventDetails;
